import React, { Fragment, PureComponent } from 'react';
import { formatHumanDate, formatHumanDateTime } from '~/DateTime';
import { Dataset as ProductDataset, DatasetExtension } from '~/ProductDataset';
import TreeBrowser from '~/TreeBrowser';
import ActivityIndicator from '~/UI/ActivityIndicator';
import { Link } from '~/UI/Button';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading/Heading';
import tableStyles from '~/UI/Table/default.less';
import Table from '~/UI/Table/Table';

import styles from '../styles.less';

interface Props {
	isFetching: boolean;
	productId: string;
	dataset: ProductDataset | null;
	emptyValue: React.ReactNode;
	onLoad(productId: string): void;
}

export default class Dataset extends PureComponent<Props> {
	componentDidMount() {
		const { productId, onLoad } = this.props;
		onLoad(productId);
	}

	componentDidUpdate(prevProps: Props) {
		const { productId, onLoad } = this.props;

		if (prevProps.productId !== productId) {
			onLoad(productId);
		}
	}

	render() {
		const { isFetching, dataset } = this.props;
		if (!dataset) {
			return isFetching ? <ActivityIndicator /> : null;
		}

		return (
			<Fragment>
				{this.renderDataset(dataset)}
				{this.renderRawData(dataset.rawData)}
				{dataset.extensions.map((extension) => (
					<Fragment key={extension.source.type}>
						{this.renderDataset(extension)}
						{this.renderRawData(extension.rawData)}
					</Fragment>
				))}
			</Fragment>
		);
	}

	renderDataset(dataset: ProductDataset | DatasetExtension) {
		const { emptyValue } = this.props;

		return (
			<Fragment>
				<Heading level={2}>{dataset.source.shortName}</Heading>

				<div className={styles.full}>
					<Table>
						<tbody>
							<tr>
								<th className={tableStyles.cell}>Country</th>
								<td className={tableStyles.cell}>
									<div className={styles.countryIcon}>
										<CountryIcon code={dataset.source.country.alpha2} name={dataset.source.country.name} />
									</div>{' '}
									{dataset.source.country.name}
								</td>
							</tr>
							<tr>
								<th className={tableStyles.cell} style={{ width: '23%' }}>
									Datasource description
								</th>
								<td className={tableStyles.cell}>{dataset.source.description}</td>
							</tr>
							{dataset.description !== null ? (
								<tr>
									<th className={tableStyles.cell}>Dataset note</th>
									<td className={tableStyles.cell}>{dataset.description}</td>
								</tr>
							) : null}
							{dataset.validFrom ? (
								<tr>
									<th className={tableStyles.cell}>Official dataset publication date</th>
									<td className={tableStyles.cell}>{formatHumanDate(dataset.validFrom)}</td>
								</tr>
							) : null}
							{dataset.productRetrievedAt ? (
								<tr>
									<th className={tableStyles.cell}>Product data accessed date</th>
									<td className={tableStyles.cell}>{formatHumanDateTime(dataset.productRetrievedAt)}</td>
								</tr>
							) : null}
							<tr>
								<th className={tableStyles.cell}>Product link</th>
								<td className={tableStyles.cell}>
									{dataset.productUrl ? (
										<Link href={dataset.productUrl} rel='noreferrer'>
											{dataset.productUrl}
										</Link>
									) : (
										emptyValue
									)}
								</td>
							</tr>
						</tbody>
					</Table>
				</div>
			</Fragment>
		);
	}

	renderRawData(rawData: { [key: string]: any }) {
		return (
			<Fragment>
				<Heading level={3}>Source data</Heading>
				<div className={styles.row}>
					{Object.keys(rawData).length > 0 ? (
						<TreeBrowser emptyValue='N/A' tree={rawData} />
					) : (
						<div className={styles.message}>Source data are not available to you</div>
					)}
				</div>
			</Fragment>
		);
	}
}
